import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, ClipboardCheck, History, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/utils/api";

const ACTIONS = {
  primary_oem: "Aramayı başlatacak OEM önerildi",
  add_oem: "Eksik OEM eklenecek",
  remove_oem: "Hatalı OEM çıkarılacak",
  review: "Kontrol edilecek",
};

const APPLIED_ACTIONS = {
  primary_oem: "Arama OEM'i yapıldı",
  add_oem: "OEM eşleşmeye eklendi",
  remove_oem: "OEM eşleşmeden çıkarıldı",
};

const RESOLUTION_COPY = {
  primary_oem: {
    title: "Arama OEM'i yap",
    description: "Önerilen OEM bu araç ve ürün filtresi için aramayı başlatan kod olarak kaydedilecek.",
    confirm: "Arama OEM'i yap ve çöz",
    button: "bg-blue-600 hover:bg-blue-700",
  },
  add_oem: {
    title: "OEM'i ekle ve çöz",
    description: "Önerilen OEM bu araç ve ürün filtresinin eşleşmelerine eklenecek.",
    confirm: "Ekle ve çöz",
    button: "bg-emerald-600 hover:bg-emerald-700",
  },
  remove_oem: {
    title: "OEM'i çıkar ve çöz",
    description: "Önerilen OEM bu araç ve ürün filtresinin eşleşmelerinden çıkarılacak.",
    confirm: "Çıkar ve çöz",
    button: "bg-red-600 hover:bg-red-700",
  },
  reject: {
    title: "Öneriyi işlemsiz kapat",
    description: "OEM bağlantılarında değişiklik yapılmadan öneri geçmişe taşınacak.",
    confirm: "İşlemsiz kapat",
    button: "bg-slate-700 hover:bg-slate-800",
  },
};

export default function OemSuggestionQueue({ refreshKey = 0, onChanged }) {
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/catalog/suggestions", { params: { status } });
      setItems(data.items || []);
      setOpenCount(data.open_count || 0);
    } catch (error) { toast.error(formatApiError(error)); }
  }, [status]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const askResolve = (item, resolution, action = null) => {
    setResolutionNote(item.note || "");
    setPending({ item, resolution, action, copy: RESOLUTION_COPY[action || "reject"] });
  };

  const resolve = async () => {
    if (!pending) return;
    const { item, resolution, action } = pending;
    setBusy(item.id);
    try {
      await api.post(`/catalog/suggestions/${item.id}/resolve`, { resolution, action, note: resolutionNote });
      toast.success(resolution === "approved" ? "OEM bağlantısı uygulandı" : "Öneri kapatıldı");
      setPending(null);
      setResolutionNote("");
      await load();
      onChanged?.();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setBusy(""); }
  };

  const applyIntended = async (item) => {
    setBusy(item.id);
    try {
      await api.post(`/catalog/suggestions/${item.id}/apply-intended`);
      toast.success("Önerilen arama OEM'i uygulandı");
      await load();
      onChanged?.();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setBusy(""); }
  };

  return <section className="rounded-xl border border-amber-200 bg-white shadow-sm">
    <button type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)} className="flex w-full items-center justify-between px-4 py-3 text-left font-bold text-[#0D1B3E]"><span className="flex items-center gap-2"><ClipboardCheck size={18}/>OEM eşleşme önerileri {openCount > 0 && <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs text-white">{openCount}</span>}</span><ChevronDown size={18} className={`transition-transform ${expanded ? "rotate-180" : ""}`}/></button>
    {expanded && <>
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-b border-amber-100 px-4 py-3">
      <p className="text-xs text-slate-500">Stok ekranından bırakılan kontroller burada sonuçlandırılır; kapanan kayıtlar geçmişte korunur.</p>
      <div className="flex rounded-lg border border-slate-200 p-1 text-xs font-semibold"><button type="button" onClick={() => setStatus("open")} className={`rounded-md px-3 py-1.5 ${status === "open" ? "bg-[#0D1B3E] text-white" : "text-slate-500"}`}>Bekleyenler</button><button type="button" onClick={() => setStatus("all")} className={`rounded-md px-3 py-1.5 ${status === "all" ? "bg-[#0D1B3E] text-white" : "text-slate-500"}`}><History size={13} className="mr-1 inline"/>Geçmiş</button></div>
    </div>
    <div className="divide-y divide-slate-100">
      {items.map((item) => <div key={item.id} className="grid gap-3 p-4 lg:grid-cols-[minmax(260px,1.4fr)_minmax(180px,1fr)_auto] lg:items-center">
        <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className="rounded bg-amber-50 px-2 py-1 text-xs font-bold text-amber-800">{ACTIONS[item.action] || item.action}</span>{item.oem && <b className="font-mono text-[#0D1B3E]">{item.oem}</b>}{item.stock_code && <span className="text-xs text-slate-500">Stok: {item.stock_code}</span>}</div><div className="mt-2 text-sm font-semibold text-slate-700">{item.vehicle_label || `${item.type_nos?.length || 0} araç tipi`}</div><div className="mt-1 text-xs text-slate-500">{item.category}{item.subcategory ? ` / ${item.subcategory}` : ""}{item.detail ? ` / ${item.detail}` : ""}{item.engine_code ? ` · Motor: ${item.engine_code}` : ""}</div>{item.note && <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">{item.note}</div>}</div>
        <div className="text-xs text-slate-500"><div>Öneren: <b>{item.created_by}</b></div><div>{item.created_at ? new Date(item.created_at).toLocaleString("tr-TR") : ""}</div>{item.status !== "open" && <div className="mt-2 font-semibold">{item.status === "approved" ? (APPLIED_ACTIONS[item.applied_action] || "Uygulandı") : "İşlemsiz kapatıldı"} · {item.resolved_by}</div>}{item.status === "approved" && item.action === "primary_oem" && item.applied_action !== "primary_oem" && <div className="mt-2 rounded bg-amber-50 px-2 py-1 font-semibold text-amber-800">Önerilen arama OEM'i henüz uygulanmadı.</div>}</div>
        {item.status === "open" && <div className="flex flex-wrap justify-end gap-2">{["primary_oem", "review"].includes(item.action) && <button type="button" disabled={busy === item.id} onClick={() => askResolve(item, "approved", "primary_oem")} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50"><CheckCircle2 size={14} className="mr-1 inline"/>Arama OEM'i yap</button>}{["add_oem", "review"].includes(item.action) && <button type="button" disabled={busy === item.id} onClick={() => askResolve(item, "approved", "add_oem")} className="rounded-lg border border-emerald-200 px-3 py-2 text-xs font-bold text-emerald-700 disabled:opacity-50">Ekle ve çöz</button>}{["remove_oem", "review"].includes(item.action) && <button type="button" disabled={busy === item.id} onClick={() => askResolve(item, "approved", "remove_oem")} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-700 disabled:opacity-50">Çıkar ve çöz</button>}<button type="button" disabled={busy === item.id} onClick={() => askResolve(item, "rejected")} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 disabled:opacity-50"><XCircle size={14} className="mr-1 inline"/>İşlemsiz kapat</button></div>}
        {item.status === "approved" && item.action === "primary_oem" && item.applied_action !== "primary_oem" && <div className="flex justify-end"><button type="button" disabled={busy === item.id} onClick={() => applyIntended(item)} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">{busy === item.id ? "Uygulanıyor..." : "Önerilen arama OEM'ini uygula"}</button></div>}
      </div>)}
      {!items.length && <div className="p-8 text-center text-sm text-slate-400">{status === "open" ? "Bekleyen OEM eşleşme önerisi yok." : "Henüz öneri geçmişi yok."}</div>}
    </div>
    </>}
    {pending && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setPending(null); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="oem-resolution-title" className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div><h3 id="oem-resolution-title" className="font-bold text-[#0D1B3E]">{pending.copy.title}</h3><p className="mt-1 text-xs text-slate-500">{pending.item.oem} · {pending.item.category}{pending.item.subcategory ? ` / ${pending.item.subcategory}` : ""}{pending.item.detail ? ` / ${pending.item.detail}` : ""}</p></div>
          <button type="button" aria-label="Kapat" disabled={Boolean(busy)} onClick={() => setPending(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-50"><X size={18}/></button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <p className="rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-700">{pending.copy.description}</p>
          <label className="block text-xs font-semibold text-slate-600">İşlem açıklaması <span className="font-normal text-slate-400">(isteğe bağlı)</span>
            <textarea value={resolutionNote} onChange={(event) => setResolutionNote(event.target.value)} rows={4} placeholder="Kontrol sonucunu veya kaynağını yazın" className="mt-2 w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-blue-500" />
          </label>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" disabled={Boolean(busy)} onClick={() => setPending(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-50">Vazgeç</button>
          <button type="button" disabled={Boolean(busy)} onClick={resolve} className={`rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${pending.copy.button}`}>{busy ? "İşleniyor..." : pending.copy.confirm}</button>
        </div>
      </div>
    </div>}
  </section>;
}
